import React, { ReactNode } from 'react';
import WidgetSection from '../widget-section/widget-section';
import { WidgetsFactoryInterface, WidgetsListInterface } from './widgets-factory.interfaces';
import { useSelector } from '@ecommerce/nest-ssr/common';
import WidgetDSAInterface from 'modules/shared/widgets/interfaces/widgets-data/widget-dsa.interface';
import WidgetDSATitle from 'modules/shared/widgets/components/widget-dsa/components/widget-dsa-title';
import Container from 'atomic/container';
import { showDSA } from 'modules/shared/widgets/components/widget-dsa/helpers/dsa-helpers';

const TabView: React.FC<{
    items: WidgetsFactoryInterface['children'];
    widgets: WidgetsListInterface;
}> = ({ items, widgets }) => {
    const valid = items.filter(item => widgets[item.type] && item.props?.data);
    const WidgetTabs = widgets.WidgetTabs;
    const WidgetTabsTab = widgets.WidgetTabsTab;
    const WidgetTabsLabel = widgets.WidgetTabsLabel;
    const WidgetTabsContent = widgets.WidgetTabsContent;

    return (
        <WidgetTabs active={0}>
            {
                valid.map((item, key) => (
                    <WidgetTabsTab key={key}>
                        <WidgetTabsLabel>{item.props?.config?.tab?.name}</WidgetTabsLabel>
                        <WidgetTabsContent>
                            { React.createElement(widgets[item.type] as any, item.props as any) }
                        </WidgetTabsContent>
                    </WidgetTabsTab>
                ))
            }
        </WidgetTabs>
    );
};

const RegularView: React.FC<{
    items: WidgetsFactoryInterface['children'];
    widgets: WidgetsListInterface;
}> = ({ items, widgets }) => {
    const valid = items.filter(item => widgets[item.type] && item.props?.data);

    return (
        <>
            {
                valid.map((item, key) => React.createElement(widgets[item.type] as any, { ...item.props, key }))
            }
        </>
    );
};

const WidgetsFactory: React.FC<{
    config: WidgetsFactoryInterface[];
    widgets: WidgetsListInterface;
    dsa?: WidgetDSAInterface;
}> = ({ config, widgets, dsa }) => {
    const pageId = useSelector((state: { pageId: string; }) => state.pageId);

    const renderDSA = (): ReactNode => {
        if (!dsa || !showDSA(dsa)) {
            return null;
        }

        return (
            <Container>
                <WidgetDSATitle dsa={dsa} />
            </Container>
        );
    };

    return (
        <>
            {renderDSA()}
            {
                // must be !== false (`=== true` won't work because old widgets might have not defined visibility property)
                config.filter(item => item.isVisible !== false).map((item, idx) => {
                    const widgetSectionId = item.campaign?.id ? `skp-track-${pageId}-${item.campaign?.id}` : void 0;

                    return (
                        <WidgetSection
                            id={widgetSectionId}
                            key={String(idx)}
                            anchor={item.anchor}
                            title={item.title}
                            link={item.link}
                            styles={item.styles}
                            badge={item.badge}
                            className={item.className}
                            dataSection={`widget-${String(idx)}`}
                        >
                            {item.viewType?.type === 'tab' && <TabView items={item.children} widgets={widgets} />}
                            {item.viewType?.type !== 'tab' && <RegularView items={item.children} widgets={widgets} />}
                        </WidgetSection>
                    );
                })
            }
        </>
    );
};

export default WidgetsFactory;
